import React, { useState } from 'react';

// Reusable confirmation modal for destructive admin actions.
// Two modes: simple (just confirm/cancel) or typed (must type a phrase to confirm).
//
// Usage:
//   <ConfirmAction
//     title="Suspend driver?"
//     description="This will prevent the driver from receiving any new bookings."
//     confirmLabel="Suspend"
//     typedConfirmation="SUSPEND"   // optional — requires typing this exact text
//     reasonRequired={true}          // optional — adds a reason text input
//     onConfirm={async (reason) => { ... }}
//     onCancel={() => setOpen(false)}
//   />
export default function ConfirmAction({
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  typedConfirmation = null,
  reasonRequired = false,
  destructive = true,
  onConfirm,
  onCancel,
}) {
  const [typedValue, setTypedValue] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const typedOk = !typedConfirmation || typedValue === typedConfirmation;
  const reasonOk = !reasonRequired || reason.trim().length >= 3;
  const canConfirm = typedOk && reasonOk && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm(reason.trim() || null);
    } catch (err) {
      // Caller is responsible for displaying error toast
      console.error('ConfirmAction onConfirm error:', err);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-5" style={{ background: 'rgba(10,22,40,0.6)', zIndex: 1000 }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-[480px] w-full" style={{ boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
        <h3 className="font-serif mt-0 mb-2" style={{ color: destructive ? '#dc2626' : '#0a1628' }}>
          {destructive && '⚠ '}{title}
        </h3>
        <p className="text-body text-[0.9rem] mb-4">
          {description}
        </p>

        {reasonRequired && (
          <div className="mb-4">
            <label className="block text-[0.85rem] font-semibold text-brand-dark mb-1">
              Reason (visible in audit log)
            </label>
            <textarea
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Repeated rider complaints..."
              className="w-full px-2.5 py-2 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]"
              style={{ fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
        )}

        {typedConfirmation && (
          <div className="mb-4">
            <label className="block text-[0.85rem] text-brand-dark mb-1">
              Type <strong style={{ color: '#dc2626' }}>{typedConfirmation}</strong> to confirm
            </label>
            <input
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              autoFocus
              className="w-full px-2.5 py-2 rounded-lg text-[0.9rem] font-mono"
              style={{ border: `1.5px solid ${typedOk ? '#2dd4a8' : '#e2e8f0'}` }}
            />
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={submitting} className="px-4 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold text-[0.9rem]">{cancelLabel}</button>
          <button onClick={handleConfirm} disabled={!canConfirm} className="px-4 py-2 rounded-lg border-0 text-white font-semibold text-[0.9rem]"
            style={{
              background: canConfirm ? (destructive ? '#dc2626' : '#2dd4a8') : '#e2e8f0',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}>{submitting ? 'Working...' : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
